import React, { useState, useEffect } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Bot,
  FileText,
  BarChart3,
  MessageSquare,
  TestTube,
  AlertCircle,
  Sparkles,
  RefreshCw,
  Loader2,
  Settings,
  History,
  FlaskConical
} from 'lucide-react';
import { AIDocumentManager } from './documents';
import { AIAgentSettings } from './agents';
import { AnalyticsDashboard } from './analytics';
import { ConversationViewer } from './conversations';
import { TestConsole } from './test';
import { supabase } from '@/utils/supabase';
import { createComponentLogger } from '@/utils/logger';

const log = createComponentLogger('AIAdminDashboard', 'ai-admin');

interface DashboardStats {
  totalDocuments: number;
  totalConversations: number;
  totalMessages: number;
  averageCost: number;
}

export default function AIAdminDashboard() {
  const [activeTab, setActiveTab] = useState('documents');
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  async function loadStats() {
    try {
      setIsLoading(true);

      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        log.warn('No user session found');
        setStats({
          totalDocuments: 0,
          totalConversations: 0,
          totalMessages: 0,
          averageCost: 0
        });
        return;
      }

      // 3-tier fallback for organization_id
      let orgId = session.user.user_metadata?.organization_id;

      // Fallback to app_metadata
      if (!orgId) {
        orgId = session.user.app_metadata?.organization_id;
      }

      // Final fallback: query users table
      if (!orgId) {
        const { data: userData } = await supabase
          .from('users')
          .select('organization_id')
          .eq('id', session.user.id)
          .single();
        orgId = userData?.organization_id;
      }

      if (!orgId) {
        log.warn('No organization ID found after all fallbacks');
        setStats({
          totalDocuments: 0,
          totalConversations: 0,
          totalMessages: 0,
          averageCost: 0
        });
        return;
      }

      // Load document count
      const { count: docCount } = await supabase
        .from('ai_documents')
        .select('*', { count: 'exact', head: true })
        .eq('organization_id', orgId);

      // Load conversation count
      const { count: convCount } = await supabase
        .from('ai_internal_chat_conversations')
        .select('*', { count: 'exact', head: true })
        .eq('organization_id', orgId);

      // Load message count and avg cost (scoped to org via conversation join)
      const { data: messageStats } = await supabase
        .from('ai_internal_chat_messages')
        .select('cost_usd, ai_internal_chat_conversations!inner(organization_id)')
        .eq('role', 'assistant')
        .eq('ai_internal_chat_conversations.organization_id', orgId)
        .not('cost_usd', 'is', null);

      const totalMessages = messageStats?.length || 0;
      const avgCost = totalMessages > 0
        ? messageStats.reduce((sum, m) => sum + (m.cost_usd || 0), 0) / totalMessages
        : 0;

      setStats({
        totalDocuments: docCount || 0,
        totalConversations: convCount || 0,
        totalMessages,
        averageCost: avgCost
      });

      log.info('Dashboard stats loaded', {
        documents: docCount,
        conversations: convCount,
        messages: totalMessages
      });
    } catch (error) {
      log.error('Failed to load dashboard stats', {}, error as Error);
      setStats({
        totalDocuments: 0,
        totalConversations: 0,
        totalMessages: 0,
        averageCost: 0
      });
    } finally {
      setIsLoading(false);
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center space-y-4">
          <Loader2 className="h-8 w-8 text-muted-foreground mx-auto animate-spin" />
          <p className="text-muted-foreground">Loading AI Admin Panel...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div className="p-2 bg-primary/10 rounded-lg">
            <Bot className="h-8 w-8 text-primary" />
          </div>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">AI Chat Administration</h1>
            <p className="text-muted-foreground">
              Manage AI documents and monitor system performance
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <Button variant="outline" size="sm" onClick={loadStats}>
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Alert Banner */}
      <Alert>
        <Sparkles className="h-4 w-4" />
        <AlertTitle>AI System Status</AlertTitle>
        <AlertDescription>
          <div className="flex items-center space-x-4 mt-2">
            <Badge variant="default" className="bg-green-100 text-green-800">
              System Operational
            </Badge>
            <span className="text-sm">
              Encompass Helper is actively responding to queries
            </span>
          </div>
        </AlertDescription>
      </Alert>

      {/* Statistics Cards */}
      {stats && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Documents</CardTitle>
              <FileText className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.totalDocuments}</div>
              <p className="text-xs text-muted-foreground">
                Knowledge base documents
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Conversations</CardTitle>
              <MessageSquare className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.totalConversations}</div>
              <p className="text-xs text-muted-foreground">
                Active chat sessions
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Messages</CardTitle>
              <BarChart3 className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.totalMessages}</div>
              <p className="text-xs text-muted-foreground">
                AI responses generated
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Avg Cost/Message</CardTitle>
              <Bot className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">${stats.averageCost.toFixed(4)}</div>
              <p className="text-xs text-muted-foreground">
                Per AI response
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="documents" className="flex items-center space-x-2">
            <FileText className="h-4 w-4" />
            <span>Documents</span>
          </TabsTrigger>
          <TabsTrigger value="agents" className="flex items-center space-x-2">
            <Settings className="h-4 w-4" />
            <span>Agents</span>
          </TabsTrigger>
          <TabsTrigger value="analytics" className="flex items-center space-x-2">
            <BarChart3 className="h-4 w-4" />
            <span>Analytics</span>
          </TabsTrigger>
          <TabsTrigger value="conversations" className="flex items-center space-x-2">
            <History className="h-4 w-4" />
            <span>History</span>
          </TabsTrigger>
          <TabsTrigger value="test" className="flex items-center space-x-2">
            <FlaskConical className="h-4 w-4" />
            <span>Test</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="documents" className="space-y-4">
          <AIDocumentManager />
        </TabsContent>

        <TabsContent value="agents" className="space-y-4">
          <AIAgentSettings />
        </TabsContent>

        <TabsContent value="analytics" className="space-y-4">
          <AnalyticsDashboard />
        </TabsContent>

        <TabsContent value="conversations" className="space-y-4">
          <ConversationViewer />
        </TabsContent>

        <TabsContent value="test" className="space-y-4">
          <TestConsole />
        </TabsContent>
      </Tabs>

      {/* Footer */}
      <div className="mt-8 pt-6 border-t">
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <div className="flex items-center space-x-2">
            <AlertCircle className="h-4 w-4" />
            <span>
              Upload documents to enable RAG-powered AI assistance
            </span>
          </div>
          <div className="flex items-center space-x-4">
            <span>Documents: {stats?.totalDocuments || 0}</span>
            <span>Messages: {stats?.totalMessages || 0}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
